import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class PhoneFormatters {
  private rawPhone: string;
  private _dbFormattedPhone: string | null = null;

  constructor(phone: string) {
    this.rawPhone = phone;
  }

  canConvertToE164US(): boolean {
    try {
      this.toE164US();
      return true;
    } catch {
      return false;
    }
  }

  dbFormattedPhone(): string | null {
    if (this._dbFormattedPhone != null) {
      return this._dbFormattedPhone;
    }

    let onlyNumbers = this.rawPhone.replace(/\D/g, '');
    if (onlyNumbers.length === 10) {
      // if 10 digit provided, assume US and prefix 1
      onlyNumbers = `1${onlyNumbers}`;
    } else if (onlyNumbers.length === 11) {
      // already has country code digit
    } else {
      return null;
    }

    // prefix + for consistent formatting, e.g. +11234567890
    onlyNumbers = `+${onlyNumbers}`;

    this._dbFormattedPhone = onlyNumbers;
    return onlyNumbers;
  }

  isValidUsNumber(): boolean {
    const regex =
      /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
    return regex.test(this.rawPhone);
  }

  toE164US(): string {
    if (!this.rawPhone) {
      throw new Error('Phone number is empty');
    }

    const digitsOnly = this.rawPhone.replace(/\D+/g, '');

    let nationalNumber: string;

    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      nationalNumber = digitsOnly.slice(1);
    } else if (digitsOnly.length === 10) {
      nationalNumber = digitsOnly;
    } else {
      throw new Error(`Invalid US phone number: "${this.rawPhone}"`);
    }

    // NANP numbers can't start with 0 or 1
    if (!/^[2-9]\d{9}$/.test(nationalNumber)) {
      throw new Error(`Invalid US phone number: "${this.rawPhone}"`);
    }

    return `+1${nationalNumber}`;
  }

  toNationalDigits(): string {
    try {
      return this.toE164US().slice(2);
    } catch {
      const digits = this.rawPhone?.replace(/\D/g, '') ?? '';
      if (digits.length === 11 && digits.startsWith('1')) {
        return digits.slice(1);
      }
      return digits.slice(0, 10);
    }
  }
}

export function usPhoneE164Validator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || String(value).trim() === '') {
      return null;
    }
    return new PhoneFormatters(String(value)).canConvertToE164US()
      ? null
      : { invalidUsPhone: true };
  };
}
